import Phaser from 'phaser';
import { ActionMap } from '@/core/ActionMap';
import type { SettingsStore } from '@/core/SettingsStore';
import { TouchJoystick } from './TouchJoystick';

export interface MoveVector {
  x: number;
  y: number;
}

/**
 * Thin adapter between Phaser input and the engine-agnostic ActionMap.
 *
 * - Listens to Phaser keyboard events and feeds them into ActionMap
 * - Polls gamepad axes for analog movement
 * - Binds to a SettingsStore so rebinding + deadzone take effect live
 */
export class InputController {
  readonly actions: ActionMap;
  readonly joystick: TouchJoystick;
  private deadzone: number;
  private disposers: Array<() => void> = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly settings: SettingsStore,
  ) {
    this.actions = new ActionMap(settings.snapshot.controls.bindings);
    this.disposers.push(this.actions.connectSettings(settings));

    this.deadzone = settings.snapshot.controls.gamepadDeadzone;
    this.disposers.push(
      settings.onChange((s) => {
        this.deadzone = s.controls.gamepadDeadzone;
      }),
    );

    const kb = scene.input.keyboard;
    if (kb) {
      const onDown = (e: KeyboardEvent) => this.actions.onKeyDown(e.code);
      const onUp = (e: KeyboardEvent) => this.actions.onKeyUp(e.code);
      kb.on('keydown', onDown);
      kb.on('keyup', onUp);
      this.disposers.push(() => {
        kb.off('keydown', onDown);
        kb.off('keyup', onUp);
      });
    }

    this.joystick = new TouchJoystick(scene);
  }

  beginFrame(): void {
    this.actions.beginFrame();
  }

  getMoveVector(): MoveVector {
    let x = 0;
    let y = 0;
    if (this.actions.isActionDown('moveUp')) y -= 1;
    if (this.actions.isActionDown('moveDown')) y += 1;
    if (this.actions.isActionDown('moveLeft')) x -= 1;
    if (this.actions.isActionDown('moveRight')) x += 1;

    const pad = this.scene.input.gamepad?.pad1;
    if (pad) {
      const ax = pad.axes[0]?.getValue() ?? 0;
      const ay = pad.axes[1]?.getValue() ?? 0;
      if (Math.abs(ax) > this.deadzone) x = ax;
      if (Math.abs(ay) > this.deadzone) y = ay;
    }

    // Joystick wins if active — touch is usually exclusive input
    if (this.joystick.isActive) {
      const j = this.joystick.vector;
      if (Math.abs(j.x) > 0.01 || Math.abs(j.y) > 0.01) {
        x = j.x;
        y = j.y;
      }
    }

    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { x, y };
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.actions.dispose();
    this.joystick.dispose();
  }
}
